import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError, API } from "@/lib/api";
import { Users, Lock, Download } from "lucide-react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

const COHORT_COLORS = {
  peer: "#FF4F00",
  direct_report: "#00A36C",
  manager: "#111111",
  self: "#6B5BD2",
};

const SCORE_BAND = (s) => {
  if (s == null) return { label: "—", tone: "text-[#999]" };
  if (s < 2) return { label: "Developing", tone: "text-[#B00020]" };
  if (s < 3) return { label: "Progressing", tone: "text-[#D97706]" };
  if (s < 4) return { label: "Meeting", tone: "text-[#00A36C]" };
  if (s < 4.5) return { label: "Exceeding", tone: "text-[#00A36C]" };
  return { label: "Role-model", tone: "text-[#111]" };
};

function _trim(label, max = 24) {
  const s = (label || "").trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export default function EmployeeSummary360() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/public/360/employee-summary/${token}`);
        if (!cancelled) setData(r.data);
      } catch (err) {
        if (!cancelled) setError(apiError(err, "Could not load your 360 summary"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const radarData = useMemo(() => {
    if (!data?.per_task) return [];
    return data.per_task.map((t, i) => ({
      task: _trim(t.description || `Task ${i + 1}`),
      full_task: t.description,
      Peers: t.peer_avg,
      "Direct reports": t.direct_report_avg,
      Manager: t.manager_score,
      Self: t.self_score,
    }));
  }, [data]);

  if (loading) {
    return <div className="max-w-5xl mx-auto px-6 py-16 font-mono-cal text-xs" data-testid="emp-summary-loading">[ LOADING YOUR 360 SUMMARY… ]</div>;
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="emp-summary-error">
        <div className="cal-card p-8 text-center">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#B00020] mb-2">[ LINK UNAVAILABLE ]</div>
          <h2 className="font-display font-bold text-2xl mb-2">We couldn't load your 360 summary</h2>
          <p className="text-sm text-[#666]">{error}</p>
          <p className="text-xs text-[#999] mt-4">The link may have been revoked. If you need a fresh link, reach out to your manager.</p>
        </div>
      </div>
    );
  }

  const counts = data.cohort_counts || {};
  const weights = data.weights || { manager: 0.5, peers: 0.25, direct_reports: 0.25 };
  const hasDr = (counts.direct_report || 0) > 0;
  const overall = data.weighted_overall;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="employee-summary-page">
      <div className="flex justify-end mb-4">
        <a href={`${API}/public/360/employee-summary/${token}/pdf`} className="cal-btn cal-btn-ghost text-xs" data-testid="emp-summary-download-pdf">
          <Download size={14} /> Download PDF
        </a>
      </div>
      {/* Header card — matches manager's results header exactly */}
      <div className="cal-card cal-shadow p-6 sm:p-8 mb-8" data-testid="emp-summary-header">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2 flex items-center gap-2">
          <Users size={12} /> [ 360° RESULTS · COHORT AGGREGATED ]
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-4xl mb-1" data-testid="emp-summary-name">
          {data.employee_name || "Your 360° summary"}
        </h1>
        <div className="text-sm text-[#666] mb-4">
          <span className="font-mono-cal text-xs">{data.employee_title}</span>
          {data.department && <span className="font-mono-cal text-xs"> · {data.department}</span>}
          <span className="font-mono-cal text-xs"> · {data.period_label}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <div className="border border-[#111] p-4 bg-[#FAFAF5]" data-testid="emp-overall-card">
            <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-1">[ WEIGHTED OVERALL ]</div>
            <div className="flex items-baseline gap-1">
              <span className="font-display font-bold text-5xl" data-testid="emp-overall-score">
                {overall != null ? overall.toFixed(2) : "—"}
              </span>
              <span className="font-mono-cal text-sm text-[#666]">/5</span>
            </div>
            <div className={`font-mono-cal text-[10px] uppercase tracking-widest mt-1 ${SCORE_BAND(overall).tone}`}>
              {SCORE_BAND(overall).label}
            </div>
          </div>
          <div className="border border-[#111] p-4" data-testid="emp-submissions-card">
            <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-2">[ SUBMISSIONS ]</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span>Peers</span><span className="font-mono-cal font-bold" data-testid="emp-count-peer">{counts.peer || 0}</span></div>
              {hasDr && (
                <div className="flex justify-between"><span>Direct reports</span><span className="font-mono-cal font-bold" data-testid="emp-count-dr">{counts.direct_report}</span></div>
              )}
              <div className="flex justify-between"><span>Manager</span><span className="font-mono-cal font-bold text-[#00A36C]" data-testid="emp-count-manager">{counts.manager ? "✓" : "—"}</span></div>
              <div className="flex justify-between"><span>Self</span><span className="font-mono-cal font-bold text-[#00A36C]" data-testid="emp-count-self">{counts.self ? "✓" : "—"}</span></div>
            </div>
          </div>
          <div className="border border-[#111] p-4" data-testid="emp-weights-card">
            <div className="font-mono-cal text-[9px] uppercase tracking-widest text-[#666] mb-2">[ COHORT WEIGHTS ]</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span>Manager</span><span className="font-mono-cal font-bold">{Math.round((weights.manager || 0) * 100)}%</span></div>
              <div className="flex justify-between"><span>Peers</span><span className="font-mono-cal font-bold">{Math.round((weights.peers || 0) * 100)}%</span></div>
              <div className="flex justify-between"><span>Direct reports</span><span className="font-mono-cal font-bold">{Math.round((weights.direct_reports || 0) * 100)}%</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Radar overlay — the only visualisation the employee gets */}
      <section className="cal-card p-6 sm:p-8" data-testid="emp-radar-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666]">[ COHORT OVERLAY · PER TASK ]</div>
            <h2 className="font-display font-bold text-xl sm:text-2xl">How each cohort sees you across tasks</h2>
          </div>
          <div className="flex flex-wrap gap-3 text-[11px] font-mono-cal uppercase tracking-widest">
            <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ backgroundColor: COHORT_COLORS.peer }} /> Peers</span>
            {hasDr && <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ backgroundColor: COHORT_COLORS.direct_report }} /> DRs</span>}
            <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ backgroundColor: COHORT_COLORS.manager }} /> Manager</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 inline-block" style={{ backgroundColor: COHORT_COLORS.self }} /> Self</span>
          </div>
        </div>

        <div className="w-full h-[420px] sm:h-[520px]" data-testid="emp-radar-chart">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="#111" strokeOpacity={0.15} />
              <PolarAngleAxis
                dataKey="task"
                tick={{ fill: "#333", fontSize: 11, fontFamily: "ui-monospace, monospace" }}
              />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 5]}
                tick={{ fill: "#999", fontSize: 10 }}
                tickCount={6}
              />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #111", fontSize: 12 }}
                formatter={(v) => (v != null ? v.toFixed(2) : "—")}
                labelFormatter={(_, items) => (items?.[0]?.payload?.full_task) || _}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
              <Radar name="Peers" dataKey="Peers" stroke={COHORT_COLORS.peer} fill={COHORT_COLORS.peer} fillOpacity={0.18} strokeWidth={2} />
              {hasDr && (
                <Radar name="Direct reports" dataKey="Direct reports" stroke={COHORT_COLORS.direct_report} fill={COHORT_COLORS.direct_report} fillOpacity={0.18} strokeWidth={2} />
              )}
              <Radar name="Manager" dataKey="Manager" stroke={COHORT_COLORS.manager} fill={COHORT_COLORS.manager} fillOpacity={0.18} strokeWidth={2} />
              <Radar name="Self" dataKey="Self" stroke={COHORT_COLORS.self} fill={COHORT_COLORS.self} fillOpacity={0.18} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Privacy / explanatory footer */}
      <div className="mt-8 cal-card p-4 bg-[#FAFAF5] border-l-4 border-[#111]" data-testid="emp-privacy-note">
        <div className="flex items-start gap-3">
          <Lock size={16} className="mt-0.5" />
          <div className="text-xs text-[#333]">
            <div className="font-display font-bold mb-1">What you're seeing — and what you're not</div>
            <div>This summary shows aggregated cohort scores so you can compare your self-rating against the view your peers, direct reports and manager hold. Individual comments and identities are never exposed. Your manager has access to the fuller view and will share any detailed feedback with you in your next 1:1.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
